import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdSearch, MdArrowBack } from 'react-icons/md';
import RepSearchBar from './rep-detail/RepSearchBar';
import RepProfileCard from './rep-detail/RepProfileCard';
import RepTagFilter from './rep-detail/RepTagFilter';
import RepPollResponses from './rep-detail/RepPollResponses';
import { useRepresentative } from '../config/RepresentativeContext';

function RepresentativesPage() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [, setSelectedRep] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [repProfile, setRepProfile] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedTags, setSelectedTags] = useState(new Set());
  const [allPollsCache, setAllPollsCache] = useState(null);
  const [pollResponses, setPollResponses] = useState([]);
  const [loadingPolls, setLoadingPolls] = useState(false);
  const [agreedPolls, setAgreedPolls] = useState(new Set());
  const [disagreedPolls, setDisagreedPolls] = useState(new Set());
  const [agreeingPoll, setAgreeingPoll] = useState(null);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);
  const pollsRef = useRef(null);
  const { repProfileId: myRepProfileId } = useRepresentative();
  const isOwnProfile = profileId && myRepProfileId && profileId === myRepProfileId;

  useEffect(() => {
    if (profileId) loadRepresentativeProfile(profileId);
  }, [profileId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- Search ---
  const searchRepresentatives = async (query) => {
    if (query.length < 2) { setSearchResults([]); setShowDropdown(false); return; }
    try {
      setSearching(true);
      const response = await axios.get(`${API_URL}/api/representatives/search`, { params: { q: query, limit: 8 } });
      setSearchResults(response.data.results || []);
      setShowDropdown(true);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchRepresentatives(value), 300);
  };

  const handleSelectResult = (rep) => {
    setSelectedRep(rep);
    setSearchQuery(rep.name);
    setShowDropdown(false);
    navigate(`/representatives/${rep.profile_id}`);
  };

  // --- Profile Loading ---
  const loadRepresentativeProfile = async (id) => {
    try {
      setLoadingProfile(true);
      setSelectedTag(null);
      setPollResponses([]);
      const response = await axios.get(`${API_URL}/api/representatives/${id}`);
      setRepProfile(response.data);
      setSelectedRep({ profile_id: id, name: `${response.data.profile.first_name} ${response.data.profile.last_name}` });
      setSearchQuery(`${response.data.profile.first_name} ${response.data.profile.last_name}`);
    } catch (error) {
      console.error('Failed to load representative:', error);
      setRepProfile(null);
    } finally {
      setLoadingProfile(false);
    }
  };

  // --- Tag Filtering ---
  const loadPollsByTag = async (tagId, tagName) => {
    const newTags = new Set(selectedTags);
    if (newTags.has(tagId)) { newTags.delete(tagId); } else { newTags.add(tagId); }
    setSelectedTags(newTags);
    setSelectedTag(newTags.size > 0 ? { tag_id: '__multi__' } : null);
    if (newTags.size === 0) { setPollResponses([]); return; }
    try {
      setLoadingPolls(true);
      let allPolls = allPollsCache;
      if (!allPolls) {
        const response = await axios.get(`${API_URL}/api/representatives/${profileId}/polls`);
        allPolls = response.data.poll_responses || [];
        setAllPollsCache(allPolls);
      }
      const filtered = allPolls.filter(p => p.tags?.some(t => newTags.has(
        repProfile.match.tag_breakdown.find(tb => tb.tag === t)?.tag_id
      )));
      setPollResponses(filtered);
      setTimeout(() => pollsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (error) {
      console.error('Failed to load polls:', error);
      setPollResponses([]);
    } finally {
      setLoadingPolls(false);
    }
  };

  // Select / deselect a whole parent-tag subtree (parent + all its children).
  // If the entire subtree is already selected, this clears it. Otherwise it
  // adds every missing subtree id to the selection.
  const loadPollsByTagGroup = async (subtreeIds) => {
    if (!subtreeIds || subtreeIds.length === 0) return;
    const current = new Set(selectedTags);
    const allSelected = subtreeIds.every((id) => current.has(id));
    const newTags = new Set(current);
    if (allSelected) {
      subtreeIds.forEach((id) => newTags.delete(id));
    } else {
      subtreeIds.forEach((id) => newTags.add(id));
    }
    setSelectedTags(newTags);
    setSelectedTag(newTags.size > 0 ? { tag_id: '__multi__' } : null);
    if (newTags.size === 0) { setPollResponses([]); return; }
    try {
      setLoadingPolls(true);
      let allPolls = allPollsCache;
      if (!allPolls) {
        const response = await axios.get(`${API_URL}/api/representatives/${profileId}/polls`);
        allPolls = response.data.poll_responses || [];
        setAllPollsCache(allPolls);
      }
      const filtered = allPolls.filter(p => p.tags?.some(t => newTags.has(
        repProfile.match.tag_breakdown.find(tb => tb.tag === t)?.tag_id
      )));
      setPollResponses(filtered);
      setTimeout(() => pollsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (error) {
      console.error('Failed to load polls:', error);
      setPollResponses([]);
    } finally {
      setLoadingPolls(false);
    }
  };

  const loadAllPolls = async () => {
    if (selectedTag?.tag_id === '__all__') { clearAllTags(); return; }
    try {
      setLoadingPolls(true);
      setSelectedTag({ tag_id: '__all__' });
      setSelectedTags(new Set());
      let allPolls = allPollsCache;
      if (!allPolls) {
        const response = await axios.get(`${API_URL}/api/representatives/${profileId}/polls`);
        allPolls = response.data.poll_responses || [];
        setAllPollsCache(allPolls);
      }
      setPollResponses(allPolls);
      setTimeout(() => pollsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (error) {
      console.error('Failed to load all polls:', error);
      setPollResponses([]);
    } finally {
      setLoadingPolls(false);
    }
  };

  const clearAllTags = () => { setSelectedTags(new Set()); setSelectedTag(null); setPollResponses([]); };

  const handleShowAllPolls = () => {
    if (selectedTag?.tag_id === '__all__') { clearAllTags(); } else { loadAllPolls(); }
  };

  // --- Clear ---
  const clearSearch = () => {
    setSearchQuery(''); setSelectedRep(null); setRepProfile(null); setSearchResults([]);
    setSelectedTag(null); setSelectedTags(new Set()); setAllPollsCache(null);
    setPollResponses([]); setAgreedPolls(new Set()); setDisagreedPolls(new Set());
    navigate('/representatives');
  };

  // --- Agree / Disagree ---
  const handleAgree = async (poll) => {
    try {
      setAgreeingPoll(poll.poll_id + '_agree');
      const method = poll.user_answered ? 'put' : 'post';
      await axios[method](`${API_URL}/api/polls/${poll.poll_id}/respond`, { answer: poll.answer });
      setAgreedPolls(prev => new Set(prev).add(poll.poll_id));
      setDisagreedPolls(prev => { const s = new Set(prev); s.delete(poll.poll_id); return s; });
      setPollResponses(prev => prev.map(p => p.poll_id === poll.poll_id ? { ...p, user_matches: true, user_answered: true } : p));
    } catch (err) { console.error('Failed to agree:', err); }
    finally { setAgreeingPoll(null); }
  };

  const handleDisagree = async (poll) => {
    try {
      setAgreeingPoll(poll.poll_id + '_disagree');
      const oppositeAnswer = poll.answer === 'Yes' ? 'No' : 'Yes';
      const method = poll.user_answered ? 'put' : 'post';
      await axios[method](`${API_URL}/api/polls/${poll.poll_id}/respond`, { answer: oppositeAnswer });
      setDisagreedPolls(prev => new Set(prev).add(poll.poll_id));
      setAgreedPolls(prev => { const s = new Set(prev); s.delete(poll.poll_id); return s; });
      setPollResponses(prev => prev.map(p => p.poll_id === poll.poll_id ? { ...p, user_matches: false, user_answered: true } : p));
    } catch (err) { console.error('Failed to disagree:', err); }
    finally { setAgreeingPoll(null); }
  };

  return (
    <div className="max-w-4xl mx-auto pt-6" data-testid="representatives-page">
      {/* Search */}
      <div className="mb-8">
        <RepSearchBar
          searchQuery={searchQuery}
          searchResults={searchResults}
          searching={searching}
          showDropdown={showDropdown}
          searchRef={searchRef}
          onSearchChange={handleSearchChange}
          onSelectResult={handleSelectResult}
          onClearSearch={clearSearch}
          onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
        />
      </div>

      {loadingProfile ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : repProfile ? (
        <div className="space-y-6">
          <RepProfileCard repProfile={repProfile} isOwnProfile={isOwnProfile} />

          <RepTagFilter
            tagBreakdown={repProfile.match.tag_breakdown}
            tagGroups={repProfile.match.tag_groups}
            selectedTags={selectedTags}
            selectedTag={selectedTag}
            onTagClick={loadPollsByTag}
            onTagGroupClick={loadPollsByTagGroup}
            onClearAllTags={clearAllTags}
            onShowAllPolls={handleShowAllPolls}
            isOwnProfile={isOwnProfile}
          />

          <RepPollResponses
            selectedTag={selectedTag}
            pollResponses={pollResponses}
            loadingPolls={loadingPolls}
            repFirstName={repProfile.profile.first_name}
            agreedPolls={agreedPolls}
            disagreedPolls={disagreedPolls}
            agreeingPoll={agreeingPoll}
            pollsRef={pollsRef}
            onAgree={handleAgree}
            onDisagree={handleDisagree}
            onClose={() => { setSelectedTag(null); setPollResponses([]); }}
            isOwnProfile={isOwnProfile}
          />

          {/* Back Button */}
          <div className="text-center">
            <button
              onClick={clearSearch}
              className="inline-flex items-center gap-2 px-6 py-3 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <MdArrowBack />
              Search for another representative
            </button>
          </div>
        </div>
      ) : (
        /* Empty State */
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
          <MdSearch className="text-6xl text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Search for a Representative</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Enter a name in the search bar above to find representatives and see how their positions compare to yours.
          </p>
        </div>
      )}
    </div>
  );
}

export default RepresentativesPage;
